import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, of, switchMap } from 'rxjs';
import { Permission } from 'src/app/common/enums/permissions';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { TalentinaService } from 'src/app/data/talentina.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AuthService } from 'src/app/core/authentication/auth.service';

/**
 * Route guard for <c>/talentina</c>. Grants access when the user has either:
 *  - the static <c>Talentina</c> permission (functional users), or
 *  - the dynamic <c>TalentinaEmployee</c> permission and passes the backend
 *    eligibility check (<c>GET /api/talentina/hasAccess</c>).
 * Redirects unauthorized users to the home page and shows an error toast.
 * Fails closed on backend errors (network/5xx/401): access is denied and the
 * user is redirected.
 */
@Injectable()
export class TalentinaAccessGuard {
  private authService = inject(AuthService);
  private talentinaService = inject(TalentinaService);
  private snackbarService = inject(SnackBarService);
  private router = inject(Router);

  async canActivate(): Promise<boolean> {
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    const hasAccess = await firstValueFrom(
      of(null).pipe(
        switchMap(() => {
          if (this.authService.hasPermission(Permission.Talentina)) {
            return of(true);
          }
          if (!this.authService.hasPermission(Permission.TalentinaEmployee)) {
            return of(false);
          }
          return this.talentinaService.hasAccess().pipe(catchError(() => of(false)));
        })
      )
    );

    if (hasAccess) {
      return true;
    }

    this.snackbarService.openErrorSnackBar(ErrorCode.NoPermission);
    this.router.navigate(['/home']);
    return false;
  }
}
